import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";
import { useLang } from "@/lib/i18n";

const NEOCLASSIC_IMG =
  "https://images.unsplash.com/photo-1700226034367-2fb120f48dfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxuZW9jbGFzc2ljJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MDA0MTQ2fDA&ixlib=rb-4.1.0&q=85";
const MINIMALIST_IMG =
  "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbHV4dXJ5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODYwMDQxNDZ8MA&ixlib=rb-4.1.0&q=85";

const ParallaxImage = ({ src, alt, testId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="overflow-hidden h-[340px] md:h-[460px]">
      <motion.img
        data-testid={testId}
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-[116%] object-cover"
      />
    </div>
  );
};

export const Manifesto = () => {
  const { copy } = useLang();
  return (
    <section id="manifesto" data-testid="manifesto-section" className="py-16 md:py-24 px-6 md:px-12 max-w-[1600px] mx-auto">
      <Reveal>
        <p className="overline-label mb-6">{copy.manifesto.overline}</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
          {copy.manifesto.titleA} <span className="italic text-gold-dark">{copy.manifesto.titleItalic}</span> {copy.manifesto.titleB}
        </h2>
      </Reveal>

      <div className="mt-12 md:mt-16 flex flex-col gap-14 md:gap-20">
        {copy.manifesto.chapters.map((c, i) => {
          const imgs = [
            { src: MINIMALIST_IMG, cap: copy.manifesto.cap2, testId: "manifesto-image-minimalist", alt: "Minimalist luxury living room" },
            { src: NEOCLASSIC_IMG, cap: copy.manifesto.cap1, testId: "manifesto-image-neoclassic", alt: "Neoclassic interior architecture" },
            { src: "/portfolio/elysium-1.jpg", cap: copy.manifesto.cap3, testId: "manifesto-image-handover", alt: "Turnkey living room, Al Hamra Villas" },
          ];
          const img = imgs[i];
          return (
            <Reveal key={c.n} delay={0.05}>
              <div data-testid={`manifesto-chapter-${i + 1}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center border-t hairline pt-10 md:pt-12">
                <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <span className="font-serif text-5xl md:text-6xl text-transparent" style={{ WebkitTextStroke: "1px rgba(46,31,39,0.35)" }}>
                    {c.n}
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl mt-4">{c.title}</h3>
                  <p className="mt-4 text-sm md:text-base font-light leading-relaxed text-plum/70 max-w-md">{c.copy}</p>
                </div>
                <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <ParallaxImage src={img.src} alt={img.alt} testId={img.testId} />
                  <p className="mt-3 text-[10px] uppercase tracking-[0.35em] text-plum/50">{img.cap}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};
